import dayjs from "dayjs";
import { db } from "../../lib/api";
import { useQuery } from "../../lib/hooks";
import { Link, setQuery, useRoute } from "../../lib/router";
import { AREA_LABEL, type Area } from "../../lib/refdata";
import { PageHead } from "../../components/Shell";
import { Empty, ErrorBox, Loading, Panel, fmtShort, money } from "../../components/ui";
import { Columns, Delta, HBars, Kpi, Meter, SERIES, THIS_YEAR, LAST_YEAR } from "../../components/charts";

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const METRIC_LABEL: Record<string, string> = {
  outreach: "New outreach", follow_ups: "Follow-ups", appointments: "Appointments", client_moves: "Client moves",
  recruit_moves: "Recruit moves", builder_moves: "Builder moves", touches: "Touches", two_way: "Two-way conversations",
  posts: "Posts", new_clients: "New clients", recruits: "Recruits", premium: "Premium", points: "Points",
};
const SIX = ["outreach", "follow_ups", "appointments", "client_moves", "recruit_moves", "builder_moves"] as const;
const pct = (n?: number | null) => (n == null ? "—" : `${Math.round(Number(n) * 100)}%`);

/** Excel-style Year + Month filter, kept in the URL so views can be shared and bookmarked. */
export function usePeriod() {
  const { query } = useRoute();
  const year = Number(query.get("y") ?? dayjs().year());
  const month = query.get("m") ? Number(query.get("m")) : null;
  const start = month ? dayjs(new Date(year, month - 1, 1)) : dayjs(new Date(year, 0, 1));
  const end = month ? start.endOf("month") : start.endOf("year");
  const label = month ? start.format("MMMM YYYY") : String(year);
  return { year, month, from: start.format("YYYY-MM-DD"), to: end.format("YYYY-MM-DD"), label };
}

export function PeriodFilter({ allowMonth = true, extra }: { allowMonth?: boolean; extra?: React.ReactNode }) {
  const p = usePeriod();
  const years = Array.from({ length: dayjs().year() - 2021 }, (_, i) => dayjs().year() - i);
  return (
    <div className="filters">
      <span className="lbl">Year</span>
      <select className="select" value={p.year} onChange={(e) => setQuery({ y: e.target.value })}>
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
      {allowMonth && <>
        <span className="lbl">Month</span>
        <select className="select" value={p.month ?? ""} onChange={(e) => setQuery({ m: e.target.value || null })}>
          <option value="">All months</option>
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
      </>}
      {extra}
      <span className="showing">Showing <b>{p.label}</b></span>
    </div>
  );
}

const TABS: { key: string; label: string; area?: Area }[] = [
  { key: "overview", label: "Scorecard" },
  { key: "daily", label: "Daily activity" },
  { key: "warm", label: "Warm market", area: "warm" },
  { key: "networking", label: "People met", area: "networking" },
  { key: "hnw", label: "HNW", area: "hnw" },
  { key: "travel", label: "Travel", area: "travel" },
  { key: "new_connections", label: "New connections", area: "new_connections" },
  { key: "results", label: "Results" },
];

export default function Dashboards() {
  const { query } = useRoute();
  const tab = TABS.find((t) => t.key === query.get("tab")) ?? TABS[0];
  const keep = (k: string) => { const q = new URLSearchParams(query); q.set("tab", k); q.set("view", "scorecard"); return `/reports?${q}`; };
  return (
    <>
      <PageHead title="Dashboards" sub="The same views as your Excel dashboards, always up to date. Pick a year and month to compare." />
      <nav className="subnav" aria-label="Dashboards">
        {TABS.map((t) => <Link key={t.key} to={keep(t.key)} aria-current={t.key === tab.key ? "page" : undefined}>{t.label}</Link>)}
      </nav>
      <PeriodFilter />
      {tab.key === "overview" && <Overview />}
      {tab.key === "daily" && <Daily />}
      {tab.area && <AreaDash area={tab.area} />}
      {tab.key === "results" && <Results />}
    </>
  );
}

function useScorecard() {
  const p = usePeriod();
  return useQuery(`dash:score:${p.from}:${p.to}`, () => db.rpc<any>("dash_scorecard", { p_from: p.from, p_to: p.to }));
}

/** Bucket the per-day rows into days / weeks / months depending on the period length. */
function bucket(days: any[], keys: readonly string[]) {
  const long = days.length > 45;
  const groups = new Map<string, any>();
  for (const d of days) {
    const k = long ? dayjs(d.day).format("YYYY-MM") : d.day;
    const g = groups.get(k) ?? { k, label: long ? dayjs(d.day).format("MMM") : dayjs(d.day).format("D"), title: long ? dayjs(d.day).format("MMMM YYYY") : dayjs(d.day).format("ddd MMM D"), ...Object.fromEntries(keys.map((x) => [x, 0])) };
    for (const x of keys) g[x] += Number(d[x] ?? 0);
    groups.set(k, g);
  }
  return { rows: [...groups.values()], long };
}

function Overview() {
  const q = useScorecard();
  const p = usePeriod();
  if (q.error) return <ErrorBox error={q.error} />;
  if (!q.data) return <Loading rows={6} />;
  const s = q.data;
  const r = s.results ?? {}, rp = s.results_prev ?? {};
  const { rows, long } = bucket(s.days ?? [], SIX);
  return (
    <div className="stack">
      <div className="kpis">
        <Kpi dark label="Average daily score" value={pct(s.avg_score)} sub={`over ${s.target_days} target days`} />
        <Kpi label="Days fully on target" value={s.days_on_target} unit={`of ${s.target_days}`} actual={s.days_on_target} target={s.target_days || undefined} />
        <Kpi label="Current streak" value={s.current_streak} unit="days" sub={`Best: ${s.best_streak} days`} />
        <Kpi label="Habits kept" value={pct(s.habits_kept)} sub="of habits with a target" />
        <Kpi label="Two-way conversations" value={s.two_way} actual={s.two_way} prev={s.two_way_prev} />
        <Kpi label="Open opportunities" value={s.open_opps} sub={<Link to="/leads/pipeline">Open pipelines →</Link>} />
      </div>

      <Panel title="Your six daily counts" hint={long ? `Monthly totals for ${p.label}` : `Each day in ${p.label}`}>
        <div className="panel-body">
          {rows.length === 0 ? <Empty title="No days in this period yet" /> :
            <Columns labels={rows.map((x) => x.label)} tipTitle={(i) => rows[i].title}
              series={SIX.map((k) => ({ key: k, label: METRIC_LABEL[k], color: SERIES[k], values: rows.map((x) => x[k]) }))} />}
        </div>
      </Panel>

      <div className="grid-2">
        <Panel title="Habits" hint="How often each habit hit its daily target">
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th>Habit</th><th className="num">Done</th><th className="num">Days hit</th><th style={{ width: "36%" }}>Kept</th></tr></thead>
              <tbody>
                {(s.habits ?? []).map((h: any) => (
                  <tr key={h.key}>
                    <td>{h.label}</td><td className="num">{h.counted}</td><td className="num">{h.hit}</td>
                    <td><div style={{ display: "flex", alignItems: "center", gap: 8 }}><div className="progress" style={{ flex: 1 }}><i style={{ width: `${Math.min(100, h.pct * 100)}%`, background: h.pct >= 0.8 ? "var(--warm)" : "var(--gold)" }} /></div><span className="small" style={{ width: 34, textAlign: "right" }}>{pct(h.pct)}</span></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
        <div className="stack">
          <Panel title="Relationship health" hint="Right now, by area">
            <div className="panel-body" style={{ display: "grid", gap: 14 }}>
              {(s.health ?? []).map((h: any) => (
                <div key={h.area}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, marginBottom: 6 }}>
                    <b style={{ color: "var(--navy)" }}>{AREA_LABEL[h.area as Area] ?? h.area}</b>
                    <span className="muted small">{h.people} people · {h.gives} gives : {h.asks} asks</span>
                  </div>
                  <Meter parts={[{ label: "Warm", value: h.warm, color: "var(--warm)" }, { label: "Cooling", value: h.cooling, color: "#d99a2b" }, { label: "Cold", value: h.cold, color: "var(--cold)" }, { label: "Giving space", value: h.giving_space, color: "#b9a6e0" }]} />
                </div>
              ))}
            </div>
          </Panel>
          {s.cycle && (
            <Panel title={`90-day cycle · week ${s.cycle_week ?? "—"}`} hint={`Started ${fmtShort(s.cycle.start)}`}>
              <div className="panel-body kpis" style={{ marginBottom: 0 }}>
                <Kpi label="Client goal" value={s.cycle.goal_clients} />
                <Kpi label="Recruit goal" value={s.cycle.goal_recruits} />
                <Kpi label="Events / month" value={s.cycle.events_per_month} />
              </div>
            </Panel>
          )}
        </div>
      </div>

      <Panel title="Results" hint={`${p.label} compared with the same period last year`}>
        <div className="panel-body kpis" style={{ marginBottom: 0 }}>
          <Kpi label="Premium" value={money(r.premium)} actual={r.premium} prev={rp.premium} />
          <Kpi label="Points" value={Number(r.points ?? 0).toLocaleString()} actual={r.points} prev={rp.points} />
          <Kpi label="New clients" value={r.new_clients} actual={r.new_clients} prev={rp.new_clients} />
          <Kpi label="Recruits" value={r.recruits} actual={r.recruits} prev={rp.recruits} />
          <Kpi label="Touches" value={r.touches} actual={r.touches} prev={rp.touches} />
          <Kpi label="Posts" value={r.posts} actual={r.posts} prev={rp.posts} />
        </div>
      </Panel>
    </div>
  );
}

function Daily() {
  const q = useScorecard();
  if (q.error) return <ErrorBox error={q.error} />;
  if (!q.data) return <Loading rows={8} />;
  const days: any[] = [...(q.data.days ?? [])].reverse();
  const on = days.filter((d) => d.targets_on);
  const tot = Object.fromEntries(SIX.map((c) => [c, days.reduce((s, d) => s + d[c], 0)]));
  const tgt = Object.fromEntries(SIX.map((c) => [c, on.reduce((s, d) => s + (d[`t_${c}`] ?? 0), 0)]));
  return (
    <Panel title="Daily activity" hint="Green = target met. Days with targets switched off still count what you did.">
      <div className="table-wrap">
        <table className="data">
          <thead><tr><th>Day</th>{SIX.map((c) => <th key={c} className="num">{METRIC_LABEL[c]}</th>)}<th className="num">Score</th></tr></thead>
          <tbody>
            {days.length === 0 && <tr><td colSpan={8}><Empty title="No days yet" /></td></tr>}
            {days.map((d) => (
              <tr key={d.day}>
                <td className="nowrap">{dayjs(d.day).format("ddd MMM D")}</td>
                {SIX.map((c) => {
                  const t = d[`t_${c}`];
                  return <td key={c} className="num" style={{ color: d.targets_on && t > 0 ? (d[c] >= t ? "var(--warm)" : "var(--ink)") : "var(--ink-3)", fontWeight: d.targets_on && t > 0 && d[c] >= t ? 600 : 400 }}>
                    {d[c]}{d.targets_on && t > 0 && <span className="muted" style={{ fontWeight: 400 }}>/{t}</span>}
                  </td>;
                })}
                <td className="num">{d.score == null ? <span className="muted">off</span> : <span className={d.on_target ? "pill warm" : "pill"}>{pct(d.score)}</span>}</td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr><td>Total</td>{SIX.map((c) => <td key={c} className="num">{tot[c]}<span className="muted" style={{ fontWeight: 400 }}>/{tgt[c]}</span></td>)}<td /></tr></tfoot>
        </table>
      </div>
    </Panel>
  );
}

function AreaDash({ area }: { area: Area }) {
  const p = usePeriod();
  const q = useQuery(`dash:area:${area}:${p.from}:${p.to}`, () => db.rpc<any>("dash_area", { p_area: area, p_from: p.from, p_to: p.to }));
  if (q.error) return <ErrorBox error={q.error} />;
  if (!q.data) return <Loading rows={6} />;
  const d = q.data, a = d.activity ?? {}, h = d.health ?? {};
  const wm = d.warm_market, ev = d.events, ps = d.pipeline_summary ?? {};
  const byMonth: any[] = d.by_month ?? [];
  return (
    <div className="stack">
      <div className="kpis">
        <Kpi dark label="People in this list" value={h.people ?? 0} sub={`${h.need_today ?? 0} need you today`} />
        <Kpi label="Touches" value={a.touches ?? 0} actual={a.touches} prev={a.touches_prev} />
        <Kpi label="Two-way conversations" value={a.two_way ?? 0} actual={a.two_way} prev={a.two_way_prev} />
        <Kpi label="People reached" value={a.people_touched ?? 0} sub={`${a.new_people ?? 0} added in period`} />
        <Kpi label="Gives : asks" value={`${a.gives ?? 0} : ${a.asks ?? 0}`} sub="Aim for 3 gives before an ask" />
        <Kpi label="Days habit met" value={a.days_on_habit ?? 0} unit={`of ${a.target_days ?? 0}`} sub={`Daily habit: ${a.habit_target ?? 0}`} actual={a.days_on_habit} target={a.target_days || undefined} />
      </div>

      <div className="grid-2">
        <Panel title="Relationship health" hint="Where everyone stands today">
          <div className="panel-body">
            <Meter parts={[{ label: "Warm", value: h.warm ?? 0, color: "var(--warm)" }, { label: "Cooling", value: h.cooling ?? 0, color: "#d99a2b" }, { label: "Cold", value: h.cold ?? 0, color: "var(--cold)" }, { label: "Giving space", value: h.giving_space ?? 0, color: "#b9a6e0" }]} />
            {(d.by_bond ?? []).length > 0 && (
              <table className="data" style={{ marginTop: 14 }}>
                <thead><tr><th>Bond</th><th className="num">Rhythm</th><th className="num">People</th><th className="num">Warm</th><th className="num">Cooling</th><th className="num">Cold</th></tr></thead>
                <tbody>{d.by_bond.map((b: any) => <tr key={b.bond}><td>{b.bond}</td><td className="num muted">{b.rhythm}d</td><td className="num">{b.people}</td><td className="num">{b.warm}</td><td className="num">{b.cooling}</td><td className="num">{b.cold}</td></tr>)}</tbody>
              </table>
            )}
          </div>
        </Panel>
        <Panel title="How far people have come" hint="Furthest stage each person has reached">
          <div className="panel-body">
            <HBars rows={(d.journey ?? []).map((j: any) => ({ label: j.label, value: j.people }))} />
          </div>
        </Panel>
      </div>

      {wm && (
        <Panel title="Warm market coverage" hint={`${wm.total} people in your warm market`}>
          <div className="panel-body">
            <Meter parts={[{ label: "In outreach now", value: wm.in_outreach, color: "var(--royal)" }, { label: "Reached before", value: Math.max(0, wm.reached - wm.in_outreach), color: "var(--sky)" }, { label: "Resting", value: wm.resting, color: "#b9a6e0" }, { label: "Never reached", value: wm.never, color: "var(--line-2)" }]} />
          </div>
        </Panel>
      )}

      {ev && (
        <Panel title="Events" hint={`Target: ${ev.target_per_month} events a month`}>
          <div className="panel-body">
            <div className="kpis">
              <Kpi label="Attended" value={ev.attended} sub={`${ev.upcoming} upcoming · ${ev.booked} booked`} />
              <Kpi label="People met" value={ev.met} />
              <Kpi label="Messaged within 48h" value={ev.within_48h} sub={`${ev.not_messaged} still to message`} actual={ev.within_48h} target={ev.met || undefined} />
              <Kpi label="Coffees" value={ev.coffees} sub={`${ev.presentations} presentations`} />
              <Kpi label="Referrals" value={ev.referrals} />
              <Kpi label="Cost" value={money(ev.cost)} />
            </div>
            {(ev.list ?? []).length > 0 && (
              <table className="data">
                <thead><tr><th>Date</th><th>Event</th><th>Status</th><th className="num">Met</th><th className="num">In 48h</th><th className="num">Coffees</th><th className="num">Cost</th></tr></thead>
                <tbody>{ev.list.map((e: any) => <tr key={e.id}><td className="nowrap">{fmtShort(e.date)}</td><td>{e.name}</td><td><span className={e.status === "Attended" ? "pill warm" : "pill"}>{e.status}</span></td><td className="num">{e.met}</td><td className="num">{e.in_48h}</td><td className="num">{e.coffees}</td><td className="num">{money(e.cost)}</td></tr>)}</tbody>
              </table>
            )}
          </div>
        </Panel>
      )}

      <div className="grid-2">
        <Panel title="Touches by month" hint={`${p.year}`}>
          <div className="panel-body">
            <Columns labels={byMonth.map((m) => MONTHS[m.month - 1])} tipTitle={(i) => `${MONTHS[i]} ${p.year}`}
              series={[{ key: "t", label: "Touches", color: THIS_YEAR, values: byMonth.map((m) => m.touches) }, { key: "w", label: "Two-way", color: "#0094c8", values: byMonth.map((m) => m.two_way) }]} />
          </div>
        </Panel>
        <Panel title="Channels" hint="Where your conversations happen">
          <div className="panel-body">
            {(d.channels ?? []).length === 0 ? <Empty title="No touches in this period" /> :
              <HBars rows={d.channels.map((c: any) => ({ label: c.channel, value: c.touches, sub: `${c.two_way} two-way` }))} color="var(--sky-ink)" />}
          </div>
        </Panel>
      </div>

      {Array.isArray(d.segments) && d.segments.length > 0 && (
        <Panel title="Segments">
          <div className="panel-body"><HBars rows={d.segments.map((s: any) => ({ label: s.segment ?? s.label ?? "—", value: s.people ?? s.count ?? 0 }))} /></div>
        </Panel>
      )}

      <Panel title="Pipeline from this list" hint={`${ps.open ?? 0} open · ${money(ps.open_value)} open value · ${ps.won ?? 0} won · ${ps.stale ?? 0} stale`}>
        <div className="table-wrap">
          <table className="data">
            <thead><tr><th>Board</th><th>Stage</th><th className="num">Open</th><th className="num">Value</th></tr></thead>
            <tbody>
              {(d.pipeline ?? []).filter((x: any) => x.open > 0).length === 0 && <tr><td colSpan={4}><Empty title="Nothing open from this list" /></td></tr>}
              {(d.pipeline ?? []).filter((x: any) => x.open > 0).map((x: any) => <tr key={x.board + x.stage}><td>{x.board}</td><td>{x.stage}</td><td className="num">{x.open}</td><td className="num">{money(x.value)}</td></tr>)}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

function Results() {
  const p = usePeriod();
  const month = p.month ? p.from : dayjs().startOf("month").format("YYYY-MM-DD");
  const q = useQuery(`dash:results:${p.year}:${month}`, () =>
    db.rpc<any>("dash_results", { p_from: `${p.year}-01-01`, p_to: `${p.year}-12-31`, p_month: month }));
  if (q.error) return <ErrorBox error={q.error} />;
  if (!q.data) return <Loading rows={6} />;
  const d = q.data, t = d.totals ?? {}, pv = d.prev ?? {}, tg = d.targets ?? {};
  const pbm: any[] = d.premium_by_month ?? [];
  return (
    <div className="stack">
      <div className="kpis">
        <Kpi dark label={`Premium ${p.year}`} value={money(t.premium)} sub={`Monthly target ${money(tg.monthly_premium)}`} />
        <Kpi label="Points" value={Number(t.points ?? 0).toLocaleString()} actual={t.points} prev={pv.points} />
        <Kpi label="New clients" value={t.new_clients} actual={t.new_clients} prev={pv.new_clients} />
        <Kpi label="Recruits" value={t.recruits} actual={t.recruits} prev={pv.recruits} sub={`Target ${tg.monthly_recruits}/month`} />
        <Kpi label="Appointments" value={t.appointments} actual={t.appointments} prev={pv.appointments} />
      </div>
      <Panel title="Premium by month" hint={`${p.year} against ${p.year - 1}`}>
        <div className="panel-body">
          <Columns labels={MONTHS} format={(n) => (n >= 1000 ? `$${Math.round(n / 100) / 10}k` : `$${Math.round(n)}`)}
            target={tg.monthly_premium ? { value: tg.monthly_premium, label: "Monthly target" } : undefined}
            series={[{ key: "ly", label: String(p.year - 1), color: LAST_YEAR, values: pbm.map((m) => m.last_year) }, { key: "ty", label: String(p.year), color: THIS_YEAR, values: pbm.map((m) => m.this_year) }]} />
        </div>
      </Panel>
      <div className="grid-2">
        <Panel title={`Week by week · ${dayjs(month).format("MMMM")}`} hint={`Weekly targets: ${money(tg.weekly_premium)} premium, ${tg.weekly_recruits} recruit`}>
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th>Week</th><th className="num">Outreach</th><th className="num">Appts</th><th className="num">Clients</th><th className="num">Recruits</th><th className="num">Premium</th></tr></thead>
              <tbody>{(d.weeks ?? []).map((w: any) => <tr key={w.week_start}><td className="nowrap">{fmtShort(w.week_start)} – {fmtShort(w.week_end)}</td><td className="num">{w.outreach}</td><td className="num">{w.appointments}</td><td className="num">{w.new_clients}</td><td className="num">{w.recruits}</td><td className="num" style={{ color: w.premium >= tg.weekly_premium ? "var(--warm)" : undefined }}>{money(w.premium)}</td></tr>)}</tbody>
            </table>
          </div>
        </Panel>
        <Panel title="Where results come from" hint="By the list each person came from">
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th>Source</th><th className="num">Clients</th><th className="num">Recruits</th><th className="num">Premium</th><th className="num">Open</th></tr></thead>
              <tbody>{(d.by_source ?? []).map((s: any) => <tr key={s.source}><td>{AREA_LABEL[s.source as Area] ?? s.source}</td><td className="num">{s.clients_won}</td><td className="num">{s.recruits}</td><td className="num">{money(s.premium)}</td><td className="num">{s.open}{s.open_value ? <span className="muted"> · {money(s.open_value)}</span> : null}</td></tr>)}</tbody>
            </table>
          </div>
        </Panel>
      </div>
      <p className="small muted">Totals vs {p.year - 1}: premium <Delta now={t.premium ?? 0} prev={pv.premium ?? 0} suffix="" />, touches <Delta now={t.touches ?? 0} prev={pv.touches ?? 0} suffix="" />.</p>
    </div>
  );
}
